import React, { useEffect, useMemo, useRef, useState } from "react";
import { fechaMX, fechaHoraMX } from "@/lib/format";
import { abrirVisor } from "@/components/VisorArchivo";
import api from "@/lib/api";
import { printCotizacionPdf, downloadCotizacionPdf, buildCotizacionBlob } from "@/lib/pdf";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ReporteModulo from "@/components/ReporteModulo";
import { CartaCampos, CartaHoja, cartaVacia } from "@/components/CartaMembretada";
import AgrupadoPorFecha from "@/components/AgrupadoPorFecha";
import FiltroSelect, { labelDe } from "@/components/FiltroSelect";
import {
  Bell, Search, Plus, Paperclip, Trash2, Pencil, Eye, Megaphone, FileText, Printer, Download, MessageCircle, Users, ChevronDown,
} from "lucide-react";

const PRIO = {
  urgente: { label: "Urgente", cls: "bg-red-100 text-red-700 border-red-300" },
  importante: { label: "Importante", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  normal: { label: "Normal", cls: "bg-gray-100 text-gray-700 border-gray-300" },
};
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx";
const REP_LOGO = "/logo-cooper.png";

const fmtFecha = (s) => (s ? fechaHoraMX(s) : "");
const avFecha = (a) => (a.created_at ? String(a.created_at).slice(0, 10) : null);
const matchQ = (a, q) => !q || `${a.titulo} ${a.mensaje || ""} ${PRIO[a.prioridad]?.label || ""}`.toLowerCase().includes(q.toLowerCase());
const Buscador = ({ q, setQ }) => (
  <div className="relative flex-1 min-w-[220px] max-w-lg">
    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
    <Input type="search" placeholder="Buscar por título, mensaje o prioridad..." className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} data-testid="aviso-search" />
  </div>
);
const kb = (n) => (!n ? "" : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function Avisos() {
  const { isAdmin } = useAuth();
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/avisos");
      setAvisos(data);
    } catch {
      toast.error("No se pudieron cargar los avisos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const descargar = async (adj) => {
    try {
      const res = await api.get(`/avisos/adjuntos/${adj.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = adj.nombre_archivo;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el archivo");
    }
  };

  const verDocumento = (adj) => abrirVisor(`/avisos/adjuntos/${adj.id}/download`, adj.nombre_archivo);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="avisos-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
          <Megaphone className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Avisos y notificaciones</h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? "Publica comunicados y archivos para tus vendedores." : "Comunicados de tu administrador."}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Cargando…</p>
      ) : isAdmin ? (
        <AdminView avisos={avisos} reload={load} descargar={descargar} verDocumento={verDocumento} />
      ) : (
        <VendedorView avisos={avisos} reload={load} descargar={descargar} verDocumento={verDocumento} />
      )}
    </div>
  );
}

/* ---------------- Vista Administrador ---------------- */
/* ---------------- Selector de destinatarios (buscador + multiselección) ---------------- */
function DestinatarioSelector({ tipo, ids, vendedores, onChange, onCommit, className }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nameById = useMemo(() => Object.fromEntries(vendedores.map((v) => [v.id, v.full_name])), [vendedores]);
  const label = tipo === "todos"
    ? "Todos los vendedores"
    : (ids.length === 0 ? "Sin destinatarios" : ids.length <= 2 ? ids.map((i) => nameById[i] || "…").join(", ") : `${ids.length} vendedores`);
  const filtered = vendedores.filter((v) => v.full_name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => {
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    onChange(next.length ? { tipo: "especificos", ids: next } : { tipo: "todos", ids: [] });
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setQ(""); onCommit && onCommit(); } }}>
      <PopoverTrigger asChild>
        <button type="button" className={`inline-flex items-center gap-1.5 border rounded-md px-2.5 py-1 text-sm bg-background hover:bg-muted ${className || ""}`} data-testid="destinatario-selector">
          <Users className="h-3.5 w-3.5 opacity-70" />
          <span className="truncate max-w-[220px]">Para: {label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b">
          <Input placeholder="Buscar vendedor…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8" data-testid="destinatario-buscar" />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          <button type="button" onClick={() => onChange({ tipo: "todos", ids: [] })} className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted text-sm" data-testid="destinatario-todos">
            <span className={`h-3.5 w-3.5 rounded-full border flex items-center justify-center ${tipo === "todos" ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
              {tipo === "todos" && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
            </span>
            Todos los vendedores
          </button>
          <div className="my-1 border-t" />
          {filtered.map((v) => (
            <label key={v.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted text-sm cursor-pointer" data-testid={`destinatario-opt-${v.id}`}>
              <Checkbox checked={ids.includes(v.id)} onCheckedChange={() => toggle(v.id)} />
              <span className="truncate">{v.full_name}{v.estatus !== "activo" ? " (inactivo)" : ""}</span>
            </label>
          ))}
          {filtered.length === 0 && <p className="px-2 py-2 text-xs text-muted-foreground">Sin resultados</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const emptyForm = { titulo: "", mensaje: "", prioridad: "normal", destinatario_tipo: "todos", destinatarios: [], activo: true };

function AdminView({ avisos, reload, descargar, verDocumento }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [saving, setSaving] = useState(false);
  const [conCarta, setConCarta] = useState(false);
  const [carta, setCarta] = useState(cartaVacia);
  const cartaHeaderRef = useRef(null);
  const cartaBodyRef = useRef(null);
  const [rep, setRep] = useState(null);
  const repHeaderRef = useRef(null);
  const repBodyRef = useRef(null);
  const [repVend, setRepVend] = useState("");
  const [q, setQ] = useState("");
  const [prioSel, setPrioSel] = useState("todos");
  const PRIO_OPTS = Object.entries(PRIO).map(([k, v]) => ({ value: k, label: v.label }));
  const visibles = avisos.filter((a) => matchQ(a, q) && (prioSel === "todos" || (a.prioridad || "normal") === prioSel));
  const nameById = useMemo(() => Object.fromEntries(vendedores.map((v) => [v.id, v.full_name])), [vendedores]);
  const destLabel = (a) => (a.destinatario_tipo === "todos" ? "Todos" : (a.destinatarios || []).map((i) => nameById[i] || "…").join(", "));

  const abrirReporteAviso = async (a) => {
    try {
      const { data } = await api.get(`/avisos/${a.id}/reporte`);
      const fecha = fechaMX(new Date());
      const secciones = [
        { titulo: `Leído por (${data.leidos.length})`, cols: ["Vendedor", "Fecha y hora"],
          filas: data.leidos.map((l) => [l.vendedor_nombre, fmtFecha(l.leido_at)]) },
        { titulo: `No leído (${data.no_leidos.length})`, cols: ["Vendedor"],
          filas: data.no_leidos.map((n) => [n.vendedor_nombre]) },
      ];
      setRep({
        docTitulo: "REPORTE DE LECTURA",
        subtitulo: `Aviso: ${data.titulo}`,
        meta: `Destinatarios: ${data.total_destinatarios} · Leídos: ${data.leidos.length} · Pendientes: ${data.no_leidos.length}`,
        fecha, secciones,
        fileName: `Reporte de lectura - ${data.titulo}`,
        resumen: `*Reporte de lectura — ${data.titulo}*\nDestinatarios: ${data.total_destinatarios}\nLeídos: ${data.leidos.length}\nPendientes: ${data.no_leidos.length}`,
      });
    } catch {
      toast.error("No se pudo generar el reporte");
    }
  };

  const abrirReporteVendedor = async (vendId) => {
    if (!vendId) return;
    try {
      const { data } = await api.get(`/avisos/reporte/vendedor/${vendId}`);
      const fecha = fechaMX(new Date());
      const secciones = [{
        titulo: `Avisos (${data.total}) — Leídos: ${data.leidos} · Pendientes: ${data.pendientes}`,
        cols: ["Aviso", "Prioridad", "Publicado", "Estado", "Fecha lectura"],
        filas: data.avisos.map((a) => [
          a.titulo, PRIO[a.prioridad]?.label || "Normal", fmtFecha(a.created_at),
          a.leido ? "Leído" : "Pendiente", a.leido_at ? fmtFecha(a.leido_at) : "—",
        ]),
      }];
      setRep({
        docTitulo: "REPORTE POR VENDEDOR",
        subtitulo: `Vendedor: ${data.vendedor_nombre}`,
        meta: `Total: ${data.total} · Leídos: ${data.leidos} · Pendientes: ${data.pendientes}`,
        fecha, secciones,
        fileName: `Reporte de avisos - ${data.vendedor_nombre}`,
        resumen: `*Reporte de avisos — ${data.vendedor_nombre}*\nTotal: ${data.total}\nLeídos: ${data.leidos}\nPendientes: ${data.pendientes}`,
      });
    } catch {
      toast.error("No se pudo generar el reporte");
    }
  };

  const repImprimir = () => printCotizacionPdf(repBodyRef.current, repHeaderRef.current, rep.fileName);
  const repDescargar = () => downloadCotizacionPdf(repBodyRef.current, repHeaderRef.current, rep.fileName);
  const repWhatsApp = async () => {
    try {
      const blob = await buildCotizacionBlob(repBodyRef.current, repHeaderRef.current);
      const file = new File([blob], `${rep.fileName}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: rep.docTitulo, text: rep.resumen });
        return;
      }
      downloadCotizacionPdf(repBodyRef.current, repHeaderRef.current, rep.fileName);
      window.open(`https://wa.me/?text=${encodeURIComponent(rep.resumen)}`, "_blank");
      toast.success("Se descargó el PDF; adjúntalo en WhatsApp Web.");
    } catch (e) {
      if (e?.name === "AbortError") return;
      window.open(`https://wa.me/?text=${encodeURIComponent(rep.resumen)}`, "_blank");
    }
  };

  useEffect(() => {
    api.get("/avisos/vendedores").then(({ data }) => setVendedores(data)).catch(() => {});
  }, []);

  const [destDraft, setDestDraft] = useState({});
  const getDest = (a) => destDraft[a.id] || { tipo: a.destinatario_tipo, ids: a.destinatarios || [] };
  const setDest = (id, val) => setDestDraft((d) => ({ ...d, [id]: val }));
  const commitDest = async (a) => {
    const draft = destDraft[a.id];
    const clear = () => setDestDraft((d) => { const n = { ...d }; delete n[a.id]; return n; });
    if (!draft) return;
    const same = draft.tipo === a.destinatario_tipo && JSON.stringify(draft.ids || []) === JSON.stringify(a.destinatarios || []);
    if (same) return clear();
    try {
      await api.patch(`/avisos/${a.id}`, { destinatario_tipo: draft.tipo, destinatarios: draft.tipo === "especificos" ? draft.ids : [] });
      toast.success("Destinatarios actualizados");
      await reload();
      window.dispatchEvent(new Event("avisos-refresh"));
    } catch {
      toast.error("No se pudo actualizar");
    } finally {
      clear();
    }
  };

  const openNew = () => { setEditing(null); setForm(emptyForm); setPendingFiles([]); setConCarta(false); setCarta(cartaVacia()); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      titulo: a.titulo, mensaje: a.mensaje || "", prioridad: a.prioridad || "normal",
      destinatario_tipo: a.destinatario_tipo || "todos", destinatarios: a.destinatarios || [], activo: !!a.activo,
    });
    setPendingFiles([]);
    setConCarta(false);
    setCarta(cartaVacia());
    setOpen(true);
  };

  const save = async () => {
    if (!form.titulo.trim()) return toast.error("El título es obligatorio");
    if (form.destinatario_tipo === "especificos" && form.destinatarios.length === 0)
      return toast.error("Selecciona al menos un vendedor");
    if (conCarta && (!carta.asunto.trim() || !carta.cuerpo.trim()))
      return toast.error("La carta necesita asunto y texto");
    setSaving(true);
    try {
      let cartaFile = null;
      if (conCarta) {
        const blob = await buildCotizacionBlob(cartaBodyRef.current, cartaHeaderRef.current);
        const nombre = `Carta - ${carta.asunto.trim().replace(/[\\/:*?"<>|]+/g, "").slice(0, 80)}.pdf`;
        cartaFile = new File([blob], nombre, { type: "application/pdf" });
      }
      let avisoId;
      if (editing) {
        await api.patch(`/avisos/${editing.id}`, form);
        avisoId = editing.id;
      } else {
        const { data } = await api.post("/avisos", form);
        avisoId = data.id;
      }
      for (const file of cartaFile ? [cartaFile, ...pendingFiles] : pendingFiles) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/avisos/${avisoId}/adjuntos`, fd);
      }
      toast.success(editing ? "Aviso actualizado" : "Aviso publicado");
      setOpen(false);
      await reload();
      window.dispatchEvent(new Event("avisos-refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el aviso");
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (a) => {
    try {
      await api.patch(`/avisos/${a.id}`, { activo: !a.activo });
      await reload();
      window.dispatchEvent(new Event("avisos-refresh"));
    } catch {
      toast.error("No se pudo cambiar el estado");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Eliminar el aviso "${a.titulo}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/avisos/${a.id}`);
      await reload();
      window.dispatchEvent(new Event("avisos-refresh"));
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const removeAdjunto = async (a, adj) => {
    if (!window.confirm(`¿Quitar el archivo "${adj.nombre_archivo}"?`)) return;
    try {
      await api.delete(`/avisos/${a.id}/adjuntos/${adj.id}`);
      await reload();
    } catch {
      toast.error("No se pudo quitar el archivo");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Buscador q={q} setQ={setQ} />
        <FiltroSelect value={prioSel} onChange={setPrioSel} todosLabel="Todas las prioridades" options={PRIO_OPTS} testid="aviso-filtro-prioridad" className="w-48" />
        <ReporteModulo
          titulo="REPORTE DE AVISOS"
          filenameBase="Reporte de avisos"
          prefix="rep-av"
          triggerTestId="aviso-reporte-btn"
          buttonVariant="outline"
          rows={visibles}
          getFecha={avFecha}
          filtros={[{ label: "Prioridad", value: labelDe(prioSel, PRIO_OPTS) }, { label: "Búsqueda", value: q }]}
          columns={[
            { header: "Aviso", value: (a) => a.titulo },
            { header: "Prioridad", value: (a) => PRIO[a.prioridad]?.label || "Normal" },
            { header: "Publicado", value: (a) => fmtFecha(a.created_at) },
            { header: "Para", value: destLabel },
            { header: "Leídos", align: "right", value: (a) => a.lecturas_count || 0 },
            { header: "Estado", value: (a) => (a.activo ? "Activo" : "Inactivo") },
          ]}
        />
        <Select value={repVend} onValueChange={(v) => { setRepVend(v); abrirReporteVendedor(v); }}>
          <SelectTrigger className="w-[230px]" data-testid="reporte-vendedor-select"><SelectValue placeholder="Reporte por vendedor…" /></SelectTrigger>
          <SelectContent>
            {vendedores.map((v) => (<SelectItem key={v.id} value={v.id}>{v.full_name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Button onClick={openNew} data-testid="nuevo-aviso-btn"><Plus className="h-4 w-4 mr-1.5" /> Nuevo aviso</Button>
      </div>

      {avisos.length === 0 && <p className="text-muted-foreground">Aún no hay avisos publicados.</p>}

      <AgrupadoPorFecha
        rows={visibles}
        getFecha={avFecha}
        testidPrefix="aviso"
        emptyText="No hay avisos en este periodo."
        renderRows={(list) => <div className="space-y-3">{list.map((a) => (
          <Card key={a.id} className={a.activo ? "" : "opacity-60"} data-testid={`aviso-card-${a.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${PRIO[a.prioridad]?.cls || PRIO.normal.cls}`}>
                      {PRIO[a.prioridad]?.label || "Normal"}
                    </span>
                    <h3 className="font-semibold">{a.titulo}</h3>
                    {!a.activo && <span className="text-xs text-muted-foreground">(inactivo)</span>}
                  </div>
                  {a.mensaje && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{a.mensaje}</p>}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground mt-2">
                    <DestinatarioSelector
                      tipo={getDest(a).tipo}
                      ids={getDest(a).ids}
                      vendedores={vendedores}
                      onChange={(val) => setDest(a.id, val)}
                      onCommit={() => commitDest(a)}
                    />
                    <span>· {fmtFecha(a.created_at)} ·</span>
                    <button className="underline hover:text-primary" onClick={() => abrirReporteAviso(a)} data-testid={`aviso-lecturas-${a.id}`}>
                      {a.lecturas_count || 0} leído(s)
                    </button>
                  </div>
                  {a.adjuntos?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {a.adjuntos.map((adj) => (
                        <span key={adj.id} className="inline-flex items-center gap-1 text-xs border rounded-md pl-2 pr-1 py-1 bg-muted/40">
                          <FileText className="h-3.5 w-3.5" />
                          <button className="hover:underline" onClick={() => verDocumento(adj)} data-testid={`adj-ver-${adj.id}`}>
                            {adj.nombre_archivo}
                          </button>
                          <button className="text-red-500 hover:bg-red-50 rounded p-0.5" onClick={() => removeAdjunto(a, adj)} title="Quitar archivo">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <div className="flex items-center gap-1.5 mr-1" title={a.activo ? "Activo — toca para desactivar" : "Inactivo — toca para activar"}>
                    <Switch checked={a.activo} onCheckedChange={() => toggleActivo(a)} data-testid={`aviso-toggle-${a.id}`} />
                    <span className={`text-xs w-14 ${a.activo ? "text-emerald-600 font-medium" : "text-muted-foreground"}`}>{a.activo ? "Activo" : "Inactivo"}</span>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(a)} title="Editar" data-testid={`aviso-edit-${a.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-red-600" onClick={() => remove(a)} title="Eliminar" data-testid={`aviso-del-${a.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}</div>}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`${conCarta ? "max-w-4xl" : "max-w-lg"} max-h-[90dvh] overflow-y-auto`}>
          <DialogHeader><DialogTitle>{editing ? "Editar aviso" : "Nuevo aviso"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} data-testid="aviso-titulo" />
            </div>
            <div>
              <Label>Mensaje</Label>
              <Textarea rows={4} value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })} data-testid="aviso-mensaje" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prioridad</Label>
                <Select value={form.prioridad} onValueChange={(v) => setForm({ ...form, prioridad: v })}>
                  <SelectTrigger data-testid="aviso-prioridad"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="importante">Importante</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Destinatarios</Label>
                <div className="mt-1">
                  <DestinatarioSelector
                    tipo={form.destinatario_tipo}
                    ids={form.destinatarios}
                    vendedores={vendedores}
                    onChange={(val) => setForm({ ...form, destinatario_tipo: val.tipo, destinatarios: val.ids })}
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2"><Paperclip className="h-4 w-4" /> Adjuntar archivos (PDF, imagen, Word, Excel)</Label>
              <input
                type="file" multiple accept={ACCEPT} className="mt-1 block w-full text-sm"
                onChange={(e) => setPendingFiles(Array.from(e.target.files || []))}
                data-testid="aviso-files"
              />
              {pendingFiles.length > 0 && (
                <ul className="mt-2 text-xs text-muted-foreground list-disc pl-5">
                  {pendingFiles.map((f, i) => <li key={i}>{f.name} · {kb(f.size)}</li>)}
                </ul>
              )}
              {editing && <p className="text-[11px] text-muted-foreground mt-1">Los archivos nuevos se agregan a los ya existentes.</p>}
            </div>

            <div className="rounded-lg border p-3 space-y-3 bg-slate-50" data-testid="carta-seccion">
              <div className="flex items-center gap-2">
                <Checkbox id="con-carta" checked={conCarta} onCheckedChange={(v) => setConCarta(!!v)} data-testid="carta-toggle" />
                <Label htmlFor="con-carta" className="cursor-pointer">Generar carta membretada (PDF)</Label>
              </div>
              {conCarta && (
                <>
                  <CartaCampos carta={carta} onChange={setCarta} />
                  <div>
                    <Label>Vista previa</Label>
                    <div className="mt-1 bg-gray-200 rounded-md p-3 overflow-x-auto" data-testid="carta-preview">
                      <CartaHoja carta={carta} headerRef={cartaHeaderRef} bodyRef={cartaBodyRef} />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">Al guardar, la carta se convierte en PDF y se adjunta al aviso. Deja el aviso inactivo si quieres revisarla antes de publicarla.</p>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} data-testid="aviso-activo" />
              <Label>Activo (visible para los vendedores)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="aviso-guardar">{saving ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rep} onOpenChange={(o) => !o && setRep(null)}>
        <DialogContent className="max-w-4xl max-h-[92dvh] overflow-y-auto">
          {rep && (
            <>
              <DialogHeader><DialogTitle>Vista previa · {rep.subtitulo}</DialogTitle></DialogHeader>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={repImprimir} data-testid="rep-imprimir"><Printer className="h-4 w-4 mr-1.5" /> Imprimir</Button>
                <Button size="sm" variant="outline" onClick={repDescargar} data-testid="rep-descargar"><Download className="h-4 w-4 mr-1.5" /> Descargar</Button>
                <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" onClick={repWhatsApp} data-testid="rep-whatsapp"><MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp</Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Vista previa del documento. Revisa y luego elige Imprimir, Descargar o WhatsApp.</p>
              <div className="overflow-x-auto bg-gray-100 p-3 rounded-md">
                <div className="bg-white text-black mx-auto shadow-sm" style={{ width: 750 }}>
                  <style>{`.pdf-exporting table td,.pdf-exporting table th{line-height:1.4!important;padding-top:4px!important;padding-bottom:4px!important}.pdf-exporting tr{page-break-inside:avoid;break-inside:avoid}`}</style>
                  <div ref={repHeaderRef} className="px-8 pt-6">
                    <div className="grid grid-cols-[auto_1fr_auto] gap-6 items-start bg-gray-50 px-3 py-3">
                      <div className="text-center">
                        <img src={REP_LOGO} className="h-16 w-16 object-contain rounded-lg mx-auto" alt="Cooper Slip" />
                        <div className="text-xs mt-1">Cooper Slip</div>
                      </div>
                      <div className="text-center text-sm font-bold leading-snug pt-3">
                        <p>Cuando tu cliente queda bien, Tu quedas mejor.</p>
                        <p>Elije las duelas que respaldan a tu negocio</p>
                      </div>
                      <div className="text-right">
                        <h2 className="text-2xl font-extrabold tracking-tight uppercase">{rep.docTitulo}</h2>
                        <p className="text-sm mt-1"><span className="font-bold">Fecha:</span> {rep.fecha}</p>
                      </div>
                    </div>
                    <div className="border-t-4 border-black" />
                  </div>
                  <div ref={repBodyRef} className="px-8 pt-4 pb-16">
                    <p className="text-sm mb-1 font-bold">{rep.subtitulo}</p>
                    <p className="text-xs mb-4">{rep.meta}</p>
                    {rep.secciones.map((s, i) => (
                      <div key={i} className="mb-5 avoid-break">
                        <p className="font-bold text-sm mb-1">{s.titulo}</p>
                        <table className="w-full text-xs border-collapse">
                          <thead><tr className="bg-gray-50">{s.cols.map((c, ci) => (<th key={ci} className="border border-gray-300 px-2 py-1 text-left">{c}</th>))}</tr></thead>
                          <tbody>
                            {s.filas.length === 0 ? (
                              <tr><td className="border border-gray-300 px-2 py-1" colSpan={s.cols.length}>Sin registros</td></tr>
                            ) : s.filas.map((f, ri) => (
                              <tr key={ri}>{s.cols.map((c, ci) => (<td key={ci} className="border border-gray-300 px-2 py-1">{f[ci] ?? ""}</td>))}</tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}

/* ---------------- Vista Vendedor ---------------- */
function VendedorView({ avisos, reload, descargar, verDocumento }) {
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");

  const abrir = async (a) => {
    setSel(a);
    if (!a.leido) {
      try {
        await api.post(`/avisos/${a.id}/leido`);
        setSel({ ...a, leido: true });
        await reload();
        window.dispatchEvent(new Event("avisos-refresh"));
      } catch { /* noop */ }
    }
  };

  if (avisos.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-16">
        <Bell className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p>No tienes avisos por ahora.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Buscador q={q} setQ={setQ} />
      <AgrupadoPorFecha
        rows={avisos.filter((a) => matchQ(a, q))}
        getFecha={avFecha}
        testidPrefix="aviso-v"
        emptyText="No hay avisos en este periodo."
        renderRows={(list) => <div className="space-y-3">{list.map((a) => (
        <Card
          key={a.id}
          className={`cursor-pointer transition-colors ${a.leido ? "" : "border-primary/60 bg-primary/5"}`}
          onClick={() => abrir(a)}
          data-testid={`aviso-item-${a.id}`}
        >
          <CardContent className="p-4 flex items-start gap-3">
            {!a.leido && <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-primary shrink-0" data-testid={`aviso-dot-${a.id}`} />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${PRIO[a.prioridad]?.cls || PRIO.normal.cls}`}>
                  {PRIO[a.prioridad]?.label || "Normal"}
                </span>
                <h3 className={`truncate ${a.leido ? "font-medium" : "font-bold"}`}>{a.titulo}</h3>
              </div>
              {a.mensaje && <p className="text-sm text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">{a.mensaje}</p>}
              <p className="text-xs text-muted-foreground mt-1">
                {fmtFecha(a.created_at)}{a.adjuntos?.length > 0 ? ` · ${a.adjuntos.length} archivo(s)` : ""}
              </p>
            </div>
          </CardContent>
        </Card>
      ))}</div>}
      />

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
          {sel && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${PRIO[sel.prioridad]?.cls || PRIO.normal.cls}`}>
                    {PRIO[sel.prioridad]?.label || "Normal"}
                  </span>
                  <DialogTitle>{sel.titulo}</DialogTitle>
                </div>
              </DialogHeader>
              <p className="text-xs text-muted-foreground -mt-2">{fmtFecha(sel.created_at)}</p>
              {sel.mensaje && <p className="text-sm whitespace-pre-wrap">{sel.mensaje}</p>}
              {sel.adjuntos?.length > 0 && (
                <div className="space-y-2 pt-2">
                  <p className="text-sm font-medium">Archivos</p>
                  {sel.adjuntos.map((adj) => (
                    <div key={adj.id} className="flex items-center justify-between border rounded-md px-3 py-2">
                      <span className="inline-flex items-center gap-2 text-sm min-w-0">
                        <FileText className="h-4 w-4 shrink-0" />
                        <span className="truncate">{adj.nombre_archivo}</span>
                        <span className="text-xs text-muted-foreground">{kb(adj.tamano)}</span>
                      </span>
                      <Button size="sm" variant="outline" onClick={() => verDocumento(adj)} data-testid={`aviso-ver-${adj.id}`}>
                        <Eye className="h-4 w-4 mr-1" /> Ver
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
